import { ApolloClient } from "@apollo/client";
import { InstalledBaseNodePathValidation } from "../graphql/queries/installedBaseNodePathValidation";
import {
    InstalledBaseNodePathValidationQuery,
    InstalledBaseNodePathValidationQueryVariables,
} from "../codegen/graphql";

/**
 * Validates a `selected` path array (from URL/localStorage) against the backend.
 *
 * Strategy: from the deepest ID up, query its canonical `pathNodes`.
 * - If the node exists: auto-correct to `[null, ...canonicalAncestors, lastId]`.
 *   This covers the parent-move case in a single round-trip.
 * - If the node does not exist (or the query fails): drop the deepest ID
 *   and try the next one.
 * - If no ID is valid: fall back to `[null]` (root).
 */
export async function validateSelectedPath(
    selected: (string | null)[],
    client: ApolloClient<object>,
): Promise<(string | null)[]> {
    const working: string[] = (selected ?? []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
    );

    while (working.length > 0) {
        const lastId = working[working.length - 1];
        let node: InstalledBaseNodePathValidationQuery["installedBaseNode"] | null | undefined;
        try {
            const result = await client.query<
                InstalledBaseNodePathValidationQuery,
                InstalledBaseNodePathValidationQueryVariables
            >({
                query: InstalledBaseNodePathValidation,
                variables: { id: lastId },
                fetchPolicy: "network-only",
            });
            node = result.data?.installedBaseNode;
        } catch {
            node = null;
        }

        if (node) {
            const ancestors = (node.pathNodes ?? [])
                .map((p) => p?.id)
                .filter((id): id is string => typeof id === "string" && id.length > 0);
            const canonical =
                ancestors.length > 0 && ancestors[ancestors.length - 1] === lastId
                    ? ancestors
                    : [...ancestors, lastId];
            return [null, ...canonical];
        }

        working.pop();
    }

    return [null];
}
